import { addDays, DateOnly } from '@if/core/date'
import { ErrorAlert } from '@if/mui'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUser } from '@src/providers/UserProvider'
import { useUnmountableDialog } from '@src/services/dialog'
import { useNextRender } from '@src/services/next-render'
import { useTranslatedPaymentMethods } from '@src/types/constants'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useCallback, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetOutstandingBalance } from '../LoanCalendar-hooks'
import { LoanCalendarActionPreviewSummaryCompact } from '../LoanCalendarActionPreview/LoanCalendarActionPreviewSummary'
import { CanSpreadOverdueAmountOnDates } from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmount-selector'
import LoanSpreadOverdueAmountDialog from '../LoanSpreadOverdueAmountDialog/LoanSpreadOverdueAmountDialog'
import CatchUpAmountOfOverdueForDateDialog from './CatchUpAmountOfOverdueForDate/CatchUpAmountOfOverdueForDateDialog'
import { EEditPaymentChangeType, useEditPaymentsAction } from './EditPayments-action'
import { useActivePaymentConfig } from './EditPayments-hooks'
import EditPaymentsSubActionMenu from './EditPaymentsSubActionMenu'
import EditPaymentTable from './EditPaymentTable'
import LoanAddPaymentDialog from './LoanAddPayment/LoanAddPaymentDialog'
import LoanAddRecurringPaymentDialog from './LoanAddRecurringPayment/LoanAddRecurringPaymentDialog'
import LoanBatchUpdatePaymentsDialog from './LoanBatchUpdatePayments/LoanBatchUpdatePaymentsDialog'
import MoveSelectedPaymentDialog from './MoveSelectedPaymentDialog'
import NoteDialog from './NoteDialog'

export enum EEditPaymentsSubAction {
  AddRecurringPayments = 'AddRecurringPayments',
  AddPayment = 'AddPayment',
  DeletePayment = 'DeletePayment',
  SpreadLateAmount = 'SpreadLateAmount',
  CatchUpAmountOfOverdueForDate = 'CatchUpAmountOfOverdueForDate',
  AddNoteToAllPayments = 'AddNoteToAllPayments',
  MovePayments = 'MovePayments',
  BatchUpdatePayments = 'BatchUpdatePayments',
  IncreasePayments = 'IncreasePayments',
}

interface EditPaymentsDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  selectedPaymentIds: Set<string>
  onConfirm: (data: SignedCalendarSimulation) => void
  onClose: () => void
  defaultSubAction?: EEditPaymentsSubAction
  isCatchUpMode?: boolean
  isIncreasingPayments?: boolean
  isLockedByOther?: boolean
}

const EditPaymentsDialog = ({
  initialSignedSimulation,
  selectedPaymentIds,
  onConfirm,
  onClose,
  defaultSubAction,
  isCatchUpMode = false,
  isIncreasingPayments = true,
  isLockedByOther = false,
}: EditPaymentsDialogProps) => {
  const { t } = useTranslation()
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  const [inCatchUpMode, setInCatchUpMode] = useState<boolean | undefined>(isCatchUpMode)
  const [inIncreasingPayments, setInIncreasingPayments] = useState<boolean | undefined>(isIncreasingPayments)
  const [subAction, setSubAction] = useState<EEditPaymentsSubAction | undefined>(defaultSubAction)
  const activationOn = initialSignedSimulation.simulation.fullLoan.loan.activationOn

  const {
    addPayment,
    deletePayment,
    deleteAllPayments,
    restorePayment,
    onCriticalChange,
    onUncriticalChange,
    payments,
    error,
    isPending,
    getConfirmedSimulation,
    currentSimulation,
    setBatchValues,
    formMethods,
    resetWithPayments,
    hasEdits,
  } = useEditPaymentsAction({
    id: initialSignedSimulation.simulation.fullLoan.loan.id,
    financingProgramId: initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
    initialSignedSimulation,
    defaultSubAction: subAction,
    selectedPaymentIds,
  })

  const {
    formState: { isValid, isLoading: isLoadingDefaultValues },
  } = formMethods

  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)

  const handleConfirm = useCallback(() => {
    void getConfirmedSimulation().then((confirmedSimulation) => {
      if (confirmedSimulation) {
        onConfirm(confirmedSimulation)
        closeDialog()
      }
    })
  }, [closeDialog, getConfirmedSimulation, onConfirm])

  const paymentMethodOptions = useTranslatedPaymentMethods()

  const handleSubActionClose = useCallback(() => {
    setSubAction(undefined)
  }, [])

  const handleOnCloseAll = useCallback(() => {
    setSubAction(undefined)
    closeDialog()
  }, [closeDialog])

  const handleResetPaymentsAndApplyAmount = useCallback(
    (fromDate: DateOnly, numberOfPayments: number, amount: number, lastPaymentAmount: number) => {
      resetWithPayments(fromDate, numberOfPayments)
      setBatchValues(
        'amount',
        (defaultAmount, isLast) => defaultAmount + (isLast ? lastPaymentAmount : amount),
        EEditPaymentChangeType.Critical,
      )
      if (inCatchUpMode) setBatchValues('sendSmsReminder', true, EEditPaymentChangeType.Critical, fromDate)
    },
    [resetWithPayments, setBatchValues, inCatchUpMode],
  )

  const loanEndDate = initialSignedSimulation.simulation.fullLoan.loan.lastPaymentOn
  const hasAmountNotReplaced = useGetOutstandingBalance(initialSignedSimulation.simulation) > 0

  const activePaymentConfig = useActivePaymentConfig(initialSignedSimulation.simulation.fullLoan.paymentConfigs)
  const defaultPaymentAmount = activePaymentConfig?.amount ?? 0

  const paymentDates = useMemo(() => payments.map((p) => p.effectiveDate), [payments])
  const canSpreadOverPayment = useMemo(
    () => CanSpreadOverdueAmountOnDates(currentSimulation.simulation, paymentDates, canBypassAcpLock),
    [currentSimulation, paymentDates, canBypassAcpLock],
  )

  const onBatchUpdatePaymentsConfirm = useCallback(
    (amount: number, fromDate?: DateOnly | null) => {
      setBatchValues('amount', (oldAmount) => oldAmount + amount, EEditPaymentChangeType.Critical, fromDate)

      // reset batch metadata
      setInCatchUpMode(undefined)
      setInIncreasingPayments(undefined)
    },
    [setBatchValues],
  )

  const canShowNestedDialogs = useNextRender()

  return (
    <>
      <FormDialog
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '80vh',
            maxHeight: '80vh',
          },
        }}
        {...dialogProps}
        title={
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="inherit">{t('loan.payment.edit')}</Typography>
              <Box>
                <LoanCalendarActionPreviewSummaryCompact
                  simulationBefore={initialSignedSimulation.simulation}
                  simulationAfter={currentSimulation.simulation}
                  actionDate={initialSignedSimulation.simulation.fullLoan.loan.lastPaymentOn}
                />
              </Box>
            </Stack>
            {error && <ErrorAlert error={error} disabledGutterBottom />}
          </Stack>
        }
        onConfirm={handleConfirm}
        dividers
        disableGutterContent
        disabled={isPending || !isValid || isLockedByOther || !hasEdits}
        loading={isPending}
        confirmColor="primary"
        disableContentPaddingTop
        maxWidth="lg"
        fullWidth
        additionalActions={
          !isLockedByOther && (
            <EditPaymentsSubActionMenu
              setSubAction={setSubAction}
              canSpreadOverdueAmount={canSpreadOverPayment}
              onSMSReminderBatchChange={(checked) =>
                setBatchValues('sendSmsReminder', checked, EEditPaymentChangeType.Uncritical)
              }
              setInCatchUpMode={setInCatchUpMode}
              setInIncreasingPayments={setInIncreasingPayments}
              hasAmountNotReplaced={hasAmountNotReplaced}
            />
          )
        }
      >
        <FormProvider {...formMethods}>
          {isLoadingDefaultValues ? (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            <EditPaymentTable
              payments={payments}
              onDelete={deletePayment}
              onDeleteAll={deleteAllPayments}
              onRestore={restorePayment}
              onCriticalChange={onCriticalChange}
              onUncriticalChange={onUncriticalChange}
              paymentMethodOptions={paymentMethodOptions}
              disabled={isLockedByOther}
              loanActivationDate={activationOn}
            />
          )}
        </FormProvider>
      </FormDialog>
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.AddRecurringPayments && (
        <LoanAddRecurringPaymentDialog
          onClose={handleSubActionClose}
          onCloseAll={handleOnCloseAll}
          loanActivationDate={activationOn}
          paymentMethodOptions={paymentMethodOptions}
          onConfirm={addPayment}
          loanEndDate={loanEndDate}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.AddPayment && (
        <LoanAddPaymentDialog
          onClose={handleSubActionClose}
          onCloseAll={handleOnCloseAll}
          loanActivationDate={activationOn}
          paymentMethodOptions={paymentMethodOptions}
          onConfirm={addPayment}
          initialAmount={defaultPaymentAmount}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.CatchUpAmountOfOverdueForDate && (
        <CatchUpAmountOfOverdueForDateDialog
          onClose={handleSubActionClose}
          onCloseAll={handleOnCloseAll}
          onConfirm={addPayment}
          simulation={currentSimulation}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.BatchUpdatePayments && (
        <LoanBatchUpdatePaymentsDialog
          simulation={currentSimulation}
          onClose={handleSubActionClose}
          onCloseAll={handleOnCloseAll}
          onConfirm={onBatchUpdatePaymentsConfirm}
          payments={payments.map((p) => ({ ...p, note: p.note ?? '' }))}
          isCatchUpMode={inCatchUpMode}
          isIncreasingPayments={inIncreasingPayments}
          onLoadPayments={inCatchUpMode ? handleResetPaymentsAndApplyAmount : undefined}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.AddNoteToAllPayments && (
        <NoteDialog
          onClose={handleSubActionClose}
          onConfirm={(note) => setBatchValues('note', note, EEditPaymentChangeType.Uncritical)}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.SpreadLateAmount && (
        <LoanSpreadOverdueAmountDialog
          initialSignedSimulation={currentSimulation}
          paymentDates={paymentDates}
          onConfirm={(amount) =>
            setBatchValues('amount', (oldAmount) => oldAmount + amount, EEditPaymentChangeType.Critical)
          }
          onClose={handleSubActionClose}
        />
      )}
      {canShowNestedDialogs && subAction === EEditPaymentsSubAction.MovePayments && (
        <MoveSelectedPaymentDialog
          onClose={handleSubActionClose}
          onConfirm={(nbDays) =>
            setBatchValues('effectiveDate', (oldDate) => addDays(oldDate, nbDays), EEditPaymentChangeType.Critical)
          }
        />
      )}
    </>
  )
}

export default EditPaymentsDialog
